import { create } from 'zustand';
import { api } from '../services/api';
import type { Reminder } from '../types';

type ReminderInput = Partial<Reminder> & { title: string; body: string | null; recurrence: Reminder['recurrence']; schedule?: { kind: 'date'; date: string; time: string } };
interface RemindersState {
  reminders: Reminder[];
  isLoading: boolean;
  error: string | null;
  fetchReminders: (params?: { status?: Reminder['status'] }) => Promise<void>;
  fetchDue: () => Promise<Reminder[]>;
  createReminder: (data: ReminderInput) => Promise<void>;
  updateReminder: (id: string, data: Partial<ReminderInput>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
}

export const useRemindersStore = create<RemindersState>((set) => ({
  reminders: [],
  isLoading: false,
  error: null,

  fetchReminders: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const query = new URLSearchParams();
      if (params?.status) query.set('status', params.status);
      const reminders = await api.get<Reminder[]>(`/api/reminders?${query}`);
      set({ reminders, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchDue: async () => {
    return api.get<Reminder[]>('/api/reminders/due');
  },

  createReminder: async (data) => {
    const reminder = await api.post<Reminder>('/api/reminders', data);
    set(state => ({ reminders: [reminder, ...state.reminders] }));
  },

  updateReminder: async (id, data) => {
    await api.patch(`/api/reminders/${id}`, data);
    const updated = await api.get<Reminder>(`/api/reminders/${id}`);
    set(state => ({ reminders: state.reminders.map(r => r.id === id ? updated : r) }));
  },

  deleteReminder: async (id) => {
    await api.delete(`/api/reminders/${id}`);
    set(state => ({ reminders: state.reminders.filter(r => r.id !== id) }));
  },
}));